import type { SVGProps } from "react";

type IconProps = Omit<SVGProps<SVGSVGElement>, "d"> & { d: string | string[]; size?: number; strokeWidth?: number };

/** Inline stroke icon. Pass one or more path `d` strings. Decorative by default. */
export function Icon({ d, size = 22, strokeWidth = 2, ...rest }: IconProps) {
  const paths = Array.isArray(d) ? d : [d];
  return (
    <svg
      aria-hidden="true"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      {...rest}
    >
      {paths.map((p) => (
        <path key={p} d={p} />
      ))}
    </svg>
  );
}

/** Arrow used in buttons and text links. Animates via the `.arr` class on hover. */
export function ArrowRight({ size = 16 }: { size?: number }) {
  return <Icon className="arr" size={size} d={["M5 12h14", "m12 5 7 7-7 7"]} />;
}

/** Common icon paths from the design canvas. */
export const iconPaths = {
  code: "M16 18l6-6-6-6M8 6l-6 6 6 6",
  dollar: "M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6",
  shield: "M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z",
  home: "M3 10.5 12 3l9 7.5V21H3zM9 21v-6h6v6",
  file: "M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8zM14 2v6h6M8 13h8M8 17h5",
  chart: "M3 3v18h18M7 15l4-4 3 3 5-6",
  play: "M5 3l14 9-14 9z",
  users:
    "M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75",
  branch: "M6 3v12M18 9a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM6 21a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM18 9a9 9 0 0 1-9 9",
  chip: "M12 3v3M12 18v3M3 12h3M18 12h3M7 7h10v10H7z",
  alert: "M12 9v4M12 17h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z",
  checkSquare: "M9 11l3 3L22 4M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11",
  chevronDown: "m6 9 6 6 6-6",
  chevronRight: "m9 18 6-6-6-6",
} as const;
